import { useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";

const WORDS = ["design", "hci", "ai", "human", "gen-ui"];
// Winter palette: background / text pairs
const STYLES: [string, string][] = [
  ["#e3e6ea", "#3a3f45"], // pale gray
  ["#e8ff8a", "#1b1f24"], // soft neon
  ["#a6e3ec", "#1b1f24"], // minty sky blue
];
const SPACING = 26; // px the cursor travels between tags
const LIFE_MS = 1400; // how long a tag stays before it's gone

// Light field where the cursor leaves a trail of keyword tags
const TagTrailBanner = () => {
  const fieldRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const field = fieldRef.current;
    if (!field) return;
    let last: { x: number; y: number } | null = null;
    let word = 0;

    const drop = (x: number, y: number) => {
      const [bg, fg] = STYLES[Math.floor(Math.random() * STYLES.length)];
      const tag = document.createElement("span");
      tag.className = "trail-tag";
      tag.textContent = WORDS[word];
      word = (word + 1) % WORDS.length;
      tag.style.left = `${x}px`;
      tag.style.top = `${y}px`;
      tag.style.background = bg;
      tag.style.color = fg;
      field.appendChild(tag);
      window.setTimeout(() => tag.remove(), LIFE_MS);
    };

    const onMove = (e: PointerEvent) => {
      const rect = field.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      if (!last || Math.hypot(x - last.x, y - last.y) >= SPACING) {
        drop(x, y);
        last = { x, y };
      }
    };
    const onLeave = () => {
      last = null;
    };

    field.addEventListener("pointermove", onMove);
    field.addEventListener("pointerleave", onLeave);
    return () => {
      field.removeEventListener("pointermove", onMove);
      field.removeEventListener("pointerleave", onLeave);
    };
  }, []);

  return (
    <Field ref={fieldRef} aria-hidden="true" />
  );
};

export default TagTrailBanner;

// Pops in, then simply vanishes when the tag is removed
const popIn = keyframes`
  0% { opacity: 0; transform: translate(-50%, -50%) scale(0.6); }
  100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
`;

const Field = styled.div`
  position: relative;
  width: 100%;
  height: 150px;
  overflow: hidden;
  background: #f9f9f9;
  touch-action: pan-y;

  .trail-tag {
    position: absolute;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.3;
    white-space: nowrap;
    pointer-events: none;
    transform: translate(-50%, -50%);
    animation: ${popIn} 160ms ease-out;
  }
`;
